/**
 * Date formatting for the notification and audit surfaces.
 *
 * Every value these panels render is an instant that arrived as an ISO string,
 * and every one of them is read forensically — "when did this fire", "how long
 * ago was she chased" — so the minute matters and the locale is pinned to
 * `en-GB` like the rest of the app rather than left to the browser.
 */

const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
};

/**
 * `2026-08-19T14:05:00Z` → `19 Aug 2026, 14:05`.
 *
 * Returns the em dash for null/undefined and for an unparseable string, so a
 * malformed timestamp shows an empty cell rather than `Invalid Date`.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', DATE_TIME_OPTIONS);
}

/** `2026-08-19` → `19 Aug 2026`. Same null/invalid handling as above. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', DATE_OPTIONS);
}

/**
 * The same, read on the **UTC** axis rather than the reader's.
 *
 * For a value that is a calendar day rather than an instant. A `@db.Date` column
 * arrives as midnight UTC, and `formatDate` would shift it to the previous day
 * for any reader west of Greenwich — turning a start date of the 1st into the
 * 31st on the audit browser of anybody in New York. The audit payloads are the
 * caller: see `auditDetailsShape.renderDetailValue`.
 */
export function formatUtcDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { ...DATE_OPTIONS, timeZone: 'UTC' });
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * A compact "how long ago" for a timestamp already shown in full elsewhere
 * (a title attribute, an adjacent column).
 *
 * The nudge dialog is the reason this exists: "chased 3 hours ago" is the fact
 * that stops a second reminder, and a reader should not have to subtract two
 * clock times under time pressure to notice it.
 */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'never';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'never';

  const elapsed = now.getTime() - then.getTime();
  // A clock skew between server and browser can put a just-written timestamp a
  // few seconds in the future; "just now" is truer there than "in -3 seconds".
  if (elapsed < MINUTE) return 'just now';
  if (elapsed < HOUR) {
    const minutes = Math.floor(elapsed / MINUTE);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (elapsed < DAY) {
    const hours = Math.floor(elapsed / HOUR);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  const days = Math.floor(elapsed / DAY);
  if (days < 30) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  return formatDate(iso);
}

/** Milliseconds within which a repeat nudge is worth warning about (24h). */
export const RECENTLY_REMINDED_MS = DAY;

/**
 * Whether somebody was chased recently enough that sending again should ask
 * for confirmation first.
 *
 * Repeat nudges are unbounded server-side **by design** — AC-16 requires a
 * deliberate repeat to actually send, so a silent cooldown would make a human's
 * action vanish. This predicate is therefore a deterrent against the accident,
 * not a control: the client is not a gate, and the backend's real defence is the
 * audit row every nudge writes.
 */
export function wasRemindedRecently(
  iso: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!iso) return false;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return false;
  const elapsed = now.getTime() - then.getTime();
  return elapsed >= 0 && elapsed < RECENTLY_REMINDED_MS;
}
